import { useState } from 'react'
import { SkipForward } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/utils'
import { DISPOSITIONS } from '../../i18n/dispositionLabels'

interface DispositionPickerProps {
  onSave: (disposition: string, notes: string) => void
  onSkip: () => void
  isSubmitting?: boolean
}

export function DispositionPicker({ onSave, onSkip, isSubmitting }: DispositionPickerProps) {
  const { t } = useTranslation('campaigns')
  const [selected, setSelected] = useState<string>('')
  const [notes, setNotes] = useState('')

  const handleSave = () => {
    if (!selected) return
    onSave(selected, notes)
  }

  return (
    <div className="rounded-lg border border-border bg-surface-card p-5">
      <h3 className="text-sm font-semibold text-text-primary">{t('dispositionPicker.title')}</h3>
      <p className="mt-1 text-xs text-text-muted">{t('dispositionPicker.subtitle')}</p>

      {/* Disposition buttons */}
      <div className="mt-3 flex flex-wrap gap-2">
        {DISPOSITIONS.map(({ value, labelKey, icon: Icon, color }) => (
          <button
            key={value}
            onClick={() => setSelected(value)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-all',
              selected === value
                ? `${color} ring-2 ring-offset-1 ring-offset-surface-card`
                : 'border-border bg-surface text-text-secondary hover:bg-surface-elevated'
            )}
          >
            <Icon className="h-4 w-4" />
            {t(labelKey)}
          </button>
        ))}
      </div>

      {/* Notes */}
      <div className="mt-4">
        <label className="mb-1 block text-xs font-medium text-text-muted">
          {t('dispositionPicker.notesLabel')}
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder={t('dispositionPicker.notesPlaceholder')}
          className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>

      {/* Actions */}
      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={!selected || isSubmitting}
          className={cn(
            'inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium text-white transition-colors',
            selected && !isSubmitting
              ? 'bg-primary hover:bg-primary/90'
              : 'cursor-not-allowed bg-primary/50'
          )}
        >
          {t('dispositionPicker.saveAndNext')}
        </button>
        <button
          onClick={onSkip}
          disabled={isSubmitting}
          className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2.5 text-sm font-medium text-text-muted transition-colors hover:bg-surface-elevated hover:text-text-secondary"
        >
          <SkipForward className="h-4 w-4" />
          {t('dispositionPicker.skip')}
        </button>
      </div>
    </div>
  )
}
